import React, { useState, useEffect } from 'react';
import { PhotoIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useProject } from '../../context/ProjectContext';
import { useToast } from '../../context/ToastContext';
import { supabase } from '../../lib/supabase';
import axios from 'axios';

export default function MediaPicker({ isOpen, onClose, onSelect }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('library'); // 'library', 'upload', or 'stock'
  const [search, setSearch] = useState('');
  const [stockImages, setStockImages] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const { currentProject } = useProject();
  const { addToast } = useToast();

  useEffect(() => {
    if (isOpen && currentProject?.id) {
      fetchMedia();
    }
  }, [isOpen, currentProject?.id]);

  const fetchMedia = async () => {
    try {
      const { data, error } = await supabase
        .from('media')
        .select('*')
        .eq('project_id', currentProject.id)
        .eq('is_archived', false)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMedia(data || []);
    } catch (error) {
      console.error('Error fetching media:', error);
      addToast('Failed to fetch media', 'error');
    } finally {
      setLoading(false);
    }
  };

  const searchUnsplash = async (query) => {
    if (!query) return;
    
    setStockLoading(true);
    try {
      const response = await axios.get(`https://api.unsplash.com/search/photos`, {
        params: {
          query,
          per_page: 30,
          client_id: process.env.REACT_APP_UNSPLASH_ACCESS_KEY
        }
      });
      setStockImages(response.data.results);
    } catch (error) {
      console.error('Error searching Unsplash:', error);
      addToast('Failed to search stock images', 'error');
    } finally {
      setStockLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${currentProject.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('content_images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('content_images')
        .getPublicUrl(filePath);

      const { data, error: dbError } = await supabase
        .from('media')
        .insert({
          project_id: currentProject.id,
          organization_id: currentProject.organization_id,
          url: publicUrl,
          filename: file.name,
          file_type: file.type.startsWith('image/') ? 'image' : 'video',
          mime_type: file.type,
          source: 'upload',
          source_id: filePath,
          metadata: {
            size: file.size,
            type: file.type
          }
        })
        .select()
        .single();

      if (dbError) throw dbError;
      
      addToast('Media uploaded successfully', 'success');
      await fetchMedia();
      onSelect(data);
      onClose();
    } catch (error) {
      console.error('Error uploading media:', error);
      addToast('Failed to upload media', 'error');
    }
  };

  const handleUnsplashSelect = async (image) => {
    
    try {
      const { data, error } = await supabase
        .from('media')
        .insert({
          project_id: currentProject.id,
          organization_id: currentProject.organization_id,
          url: image.urls.regular,
          filename: `unsplash-${image.id}`,
          file_type: 'image',
          mime_type: 'image/jpeg',
          source: 'unsplash',
          source_id: image.id,
          metadata: {
            attribution: {
              name: image.user.name,
              username: image.user.username,
              link: image.user.links.html
            },
            description: image.description,
            dimensions: {
              width: image.width,
              height: image.height
            }
          }
        })
        .select()
        .single();

      if (error) throw error;
      
      addToast('Stock image added successfully', 'success');
      onSelect(data);
      onClose();
    } catch (error) {
      console.error('Error adding stock image:', error);
      addToast('Failed to add stock image', 'error');
    }
  };

  const handleSearch = (e) => {
    e?.preventDefault(); // Make the event parameter optional
    if (search.trim()) {
      searchUnsplash(search.trim());
    }
  };

  const handleSelect = (item) => {
    onSelect(item);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999]">
      <div className="absolute inset-0" />
      
      <div className="relative flex items-center justify-center h-full">
        <div 
          className="absolute inset-0 bg-black/80" 
          onClick={onClose}
        />
        <div className="relative bg-dark-200 m-[100px] rounded-lg w-[calc(100%-200px)] h-[calc(100%-200px)] flex flex-col">
          <div className="flex justify-between items-center px-6 py-4 border-b border-divider bg-dark-300 rounded-t-lg">
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => setView('library')}
                className={`px-4 py-2 rounded-lg ${view === 'library' ? 'bg-accent-primary text-white' : 'hover:bg-dark-400'}`}
              >
                Media Library
              </button>
              <label className={`px-4 py-2 rounded-lg cursor-pointer ${view === 'upload' ? 'bg-accent-primary text-white' : 'hover:bg-dark-400'}`}>
                <input
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={handleFileUpload}
                />
                Upload New
              </label>
              <button
                type="button"
                onClick={() => {
                  setView('stock');
                  if (!stockImages.length && search) {
                    handleSearch();
                  }
                }}
                className={`px-4 py-2 rounded-lg ${view === 'stock' ? 'bg-accent-primary text-white' : 'hover:bg-dark-400'}`}
              >
                Stock Images
              </button>
            </div>
            <button 
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-dark-400 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6 text-gray-400 hover:text-white" />
            </button>
          </div>

          <div className="p-6 flex-1 overflow-y-auto">
            {view === 'stock' && (
              <div className="mb-6">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearch();
                        }
                      }}
                      placeholder="Search for stock images..."
                      className="w-full px-4 py-2 pl-10 bg-dark-300 border border-divider rounded-lg focus:outline-none focus:border-accent-primary"
                    />
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                  </div>
                  <button
                    type="button"
                    onClick={handleSearch}
                    className="px-4 py-2 bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-colors"
                  >
                    Search
                  </button>
                </div>
              </div>
            )}

            {view === 'library' && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {loading ? (
                  <div className="col-span-full flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
                  </div>
                ) : (
                  media.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className="group relative aspect-video rounded-lg overflow-hidden hover:ring-2 hover:ring-accent-primary focus:outline-none focus:ring-2 focus:ring-accent-primary bg-dark-300"
                    >
                      <img
                        src={item.url}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-sm">Select</span>
                      </div>
                      {item.source === 'unsplash' && item.metadata?.attribution && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1">
                          Photo by {item.metadata.attribution.name}
                        </div>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}

            {view === 'stock' && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {stockLoading ? (
                  <div className="col-span-full flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary"></div>
                  </div>
                ) : (
                  stockImages.map((image) => (
                    <button
                      key={image.id}
                      onClick={(e) => {
                        e.preventDefault();
                        handleUnsplashSelect(image)
                      }}
                      className="group relative aspect-video rounded-lg overflow-hidden hover:ring-2 hover:ring-accent-primary focus:outline-none focus:ring-2 focus:ring-accent-primary bg-dark-300"
                    >
                      <img
                        src={image.urls.small}
                        alt={image.alt_description}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-sm">Select</span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1">
                        Photo by {image.user.name}
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 